/**
 * COMPANY-WORKSPACE-F2 — explicit corporate manager mode helpers.
 * Backend capabilities remain authoritative; these helpers only adapt transport.
 */

export const MANAGER_MODE_PERSONAL = "personal";
export const MANAGER_MODE_PARTNER = "partner";
export const MANAGER_MODE_COMPANY = "company";

export function isCompanyManagerMode(managerContext) {
  return Boolean(managerContext && managerContext.mode === MANAGER_MODE_COMPANY);
}

export function buildCompanyManagerContext({
  companyId,
  actorMembershipGeneration,
  canCreate = false,
  members = [],
  searchClients = null,
}) {
  if (companyId == null || actorMembershipGeneration == null) return null;
  return {
    mode: MANAGER_MODE_COMPANY,
    companyId,
    actorMembershipGeneration,
    canCreate: Boolean(canCreate),
    members: Array.isArray(members) ? members : [],
    searchClients: typeof searchClients === "function" ? searchClients : null,
  };
}

export function presentationCapabilities(share) {
  return (share && share.capabilities) || {};
}

export function canCap(share, key) {
  return Boolean(presentationCapabilities(share)[key]);
}

export function corporateRevisionOf(share) {
  if (!share || share.corporate_revision == null) return null;
  return share.corporate_revision;
}

export function actorGenerationOf(managerContext, share) {
  if (share && share.actor_membership_generation != null) {
    return share.actor_membership_generation;
  }
  return managerContext?.actorMembershipGeneration ?? null;
}

export function withCorporateConcurrency(managerContext, share, extra = {}) {
  const generation = actorGenerationOf(managerContext, share);
  const expectedRevision = corporateRevisionOf(share);
  const body = {
    ...extra,
    actor_membership_generation: generation,
  };
  if (expectedRevision != null) {
    body.expected_revision = expectedRevision;
  }
  return body;
}

/** Map personal write payload → F1 corporate create/update body. */
export function toCorporateWriteBody({
  personalPayload,
  propertyId,
  managerContext,
  share = null,
  confirmCompanyFallback = false,
  responsibleUserId = null,
}) {
  const source = personalPayload || {};
  const body = {
    actor_membership_generation: actorGenerationOf(managerContext, share),
  };

  if (!share) {
    body.property_id = propertyId;
    if (responsibleUserId != null) {
      body.responsible_user_id = responsibleUserId;
    }
  } else if (share.corporate_revision != null) {
    body.expected_revision = share.corporate_revision;
  }

  if (Object.prototype.hasOwnProperty.call(source, "client")) {
    body.client_id = source.client;
  }
  [
    "internal_label",
    "price_mode",
    "custom_price",
    "custom_currency",
    "location_precision",
    "presentation_note",
    "include_video",
    "expires_at",
    "media",
  ].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      body[key] = source[key];
    }
  });

  // Contact fields are create-only on corporate create; updates use /contact/.
  if (!share) {
    ["contact_phone", "contact_email", "include_contact_avatar"].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        body[key] = source[key];
      }
    });
    if (confirmCompanyFallback) {
      body.confirm_company_fallback = true;
    }
  }

  return body;
}

export function toCorporateContactBody(managerContext, share, contactDraft) {
  const draft = contactDraft || {};
  const body = withCorporateConcurrency(managerContext, share, {
    contact_phone: draft.contact_phone || "",
    contact_email: draft.contact_email || "",
    include_contact_avatar: Boolean(draft.include_contact_avatar),
  });

  // F1 omit semantics: missing confirm_company_fallback preserves existing fallback.
  // Only send when the caller explicitly set the key (confirm or intentional withdraw).
  if (Object.prototype.hasOwnProperty.call(draft, "confirm_company_fallback")) {
    body.confirm_company_fallback = Boolean(draft.confirm_company_fallback);
  }

  const dirty = draft.dirtyFallbackFields || {};
  [
    "fallback_contact_name",
    "fallback_contact_phone",
    "fallback_contact_email",
  ].forEach((key) => {
    if (dirty[key]) {
      body[key] = draft[key] || "";
    }
  });
  if (dirty.use_fallback_contact) {
    body.use_fallback_contact = Boolean(draft.use_fallback_contact);
  }

  return body;
}

export function isCorporateConflictError(error) {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  if (status !== 409) return false;
  // Stale-conflict refetch path is only for concurrency tokens — not business 409s.
  return code === "generation_mismatch" || code === "revision_conflict";
}

export function messageFromCorporateError(error) {
  const data = error?.response?.data;
  const code = data?.code;
  if (code === "generation_mismatch") {
    return "Стан членства змінився. Оновіть дані та повторіть навмисно.";
  }
  if (code === "revision_conflict") {
    return "Презентацію змінено іншим учасником. Оновіть дані та повторіть навмисно.";
  }
  if (code === "presentation_cap_exceeded") {
    return "Досягнуто ліміт активних корпоративних презентацій.";
  }
  if (code === "handover_required") {
    return "Спочатку потрібна явна передача відповідальності.";
  }
  if (code === "insufficient_publication_authority") {
    return "Немає права публікації для цієї дії.";
  }
  if (code === "insufficient_client_authority") {
    return "Немає права на прив'язку цього клієнта.";
  }
  if (code === "archive_mutation_forbidden") {
    return "Компанію архівовано; ця дія недоступна.";
  }
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail;
  }
  if (error?.message) return error.message;
  return "Помилка запиту";
}

export const CORPORATE_AUDIT_ACTION_LABELS = {
  create: "Створення",
  update: "Оновлення",
  revoke: "Відкликання",
  reactivate: "Відновлення",
  rotate: "Оновлення посилання",
  handover: "Передача відповідальності",
  contact: "Оновлення контакту",
  fallback_on_end: "Fallback після завершення членства",
  delete: "Видалення",
};

export function corporateAuditLabel(action) {
  return CORPORATE_AUDIT_ACTION_LABELS[action] || action || "Подія";
}

export function clearUnsafeClientProjection(share) {
  if (!share) return share;
  const caps = presentationCapabilities(share);
  if (caps.client_safe_read === false) {
    return { ...share, client: null };
  }
  return share;
}

export function filterPresentationsForProperty(rows, propertyId) {
  if (propertyId == null) return Array.isArray(rows) ? rows : [];
  const id = String(propertyId);
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (row == null) return false;
    if (row.property_id != null) return String(row.property_id) === id;
    if (row.property && row.property.id != null) {
      return String(row.property.id) === id;
    }
    // Missing property identity is not safe to keep in a property-scoped manager.
    return false;
  });
}
